import React, { useContext, useMemo } from "react";
import { ElementState, Preview } from "@creatomate/preview";
import { MillisecondsField } from "@culturehq/components";
import { MillisecondsFieldValue } from "@culturehq/components/dist/components/form/MillisecondsField";
import { Trans } from "app/utils";
import { ElementSource } from "chq";
import { CreatomateContext } from "../../contexts/VideoStudioContext";
import { isSubtitleText, getSubtitleVideoRef } from "../../sidebar/tabs/subtitle/utils";
import { isStoryElement } from "../../utils";

interface VideoSpeedProps {
  activeElement: ElementState;
  preview: Preview;
}

const VideoSpeed: React.FC<VideoSpeedProps> = ({ activeElement, preview }: VideoSpeedProps) => {
  const { handleUpdateData } = useContext(CreatomateContext);
  const { elements }: {elements: ElementState[]} = preview.state || { elements: [] };

  const subtitleElements: ElementState[] = useMemo(() => (
    elements.filter(element => isSubtitleText(element.source.name))
  ), [elements]);

  const currentSpeed = useMemo(() => {
    const speedValue = activeElement?.source?.speed
      ? parseInt(activeElement.source.speed.replace("%", ""), 10)
      : 100;
    return speedValue / 100;
  }, [activeElement.source.speed]);

  const handleSpeedChange = async (value: MillisecondsFieldValue) => {
    if (value && value >= 50 && value <= 200) {
      const valueDecimal = value / 100;
      await preview.applyModifications({
        [`${activeElement.source.id}.speed`]: value,
        [`${activeElement.source.id}.duration`]: (activeElement.source.duration * currentSpeed) / valueDecimal
      });
      if (!subtitleElements.length) handleUpdateData({ blockSubtitles: true });
      else if (isStoryElement(activeElement.source)) {
        const source = preview.getSource();
        await preview.setSource({
          ...source,
          elements: source.elements.map((element: ElementSource) => {
            if (!isSubtitleText(element.name)) return element;
            if (getSubtitleVideoRef(element.name)[1] !== activeElement.source.id) {
              return element;
            }
            return {
              ...element,
              time: activeElement.source.time + (
                element.time - activeElement.source.time) * (currentSpeed / valueDecimal),
              duration: (element.duration * currentSpeed) / valueDecimal
            };
          })
        });
      }
    }
  };

  const getSpeedValue = (): number => {
    if (!activeElement?.source?.speed) return 100;
    return parseInt(activeElement.source.speed.replace("%", ""), 10);
  };

  return (
    <MillisecondsField
      className="chq-ffd-border"
      min={0.5}
      max={2}
      name="speed"
      onChange={handleSpeedChange}
      value={getSpeedValue()}
    >
      <Trans>Video Speed</Trans>
    </MillisecondsField>
  );
};

export default VideoSpeed;
